/**
 * Direct password grant against the realm's custom login endpoint.
 *
 * This realm does not offer a standard OIDC redirect flow to us; it exposes an
 * endpoint that returns tokens directly. That is why this exists alongside the
 * `oidc` mode rather than replacing it.
 *
 * The access token is held in memory only. It is a bearer credential, and
 * localStorage is readable by any XSS on the origin -- a token there is a token
 * exfiltrated. The cost is that a page reload requires signing in again, which
 * is the correct trade for a credential that grants query access to a patient
 * data lake.
 */

export interface PasswordTokens {
  readonly accessToken: string;
  readonly refreshToken: string | null;
  /** Epoch ms at which the access token expires. */
  readonly expiresAt: number;
}

export interface TokenClaims {
  readonly sub: string;
  readonly name: string | null;
  readonly preferredUsername: string | null;
  readonly email: string | null;
  readonly roles: readonly string[];
  readonly audience: readonly string[];
}

export class AuthError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'AuthError';
    this.status = status;
  }
}

interface LoginResponse {
  readonly access_token?: string;
  readonly refresh_token?: string;
  readonly expires_in?: number;
  readonly id_token?: string;
}

/** Base64url decode, tolerant of missing padding. */
function decodeSegment(segment: string): string {
  const normalised = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalised.padEnd(normalised.length + ((4 - (normalised.length % 4)) % 4), '=');
  const binary = atob(padded);
  // The token may contain non-ASCII (Arabic display names), so decode as UTF-8.
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Reads the claims this app needs.
 *
 * Decoding only -- never verification. The token is verified by the backend on
 * every request; a client-side check would prove nothing to anyone.
 */
export function decodeClaims(accessToken: string): TokenClaims | null {
  const parts = accessToken.split('.');
  if (parts.length < 2 || !parts[1]) return null;

  try {
    const payload = JSON.parse(decodeSegment(parts[1])) as Record<string, unknown>;
    const sub = typeof payload['sub'] === 'string' ? payload['sub'] : '';
    if (sub === '') return null;

    const realmAccess = payload['realm_access'];
    const roles =
      typeof realmAccess === 'object' && realmAccess !== null
        ? ((realmAccess as { roles?: unknown }).roles ?? [])
        : [];

    const rawAudience = payload['aud'];
    const audience = Array.isArray(rawAudience)
      ? rawAudience.filter((entry): entry is string => typeof entry === 'string')
      : typeof rawAudience === 'string'
        ? [rawAudience]
        : [];

    return {
      // `sub` is the stable identity. Email can change and is not unique.
      sub,
      name: typeof payload['name'] === 'string' ? payload['name'] : null,
      preferredUsername:
        typeof payload['preferred_username'] === 'string' ? payload['preferred_username'] : null,
      email: typeof payload['email'] === 'string' ? payload['email'] : null,
      roles: Array.isArray(roles)
        ? roles.filter((entry): entry is string => typeof entry === 'string')
        : [],
      audience,
    };
  } catch {
    return null;
  }
}

export function expiryFromResponse(body: LoginResponse, now = Date.now()): number {
  const seconds = typeof body.expires_in === 'number' && body.expires_in > 0 ? body.expires_in : 300;
  return now + seconds * 1000;
}

function toTokens(body: LoginResponse): PasswordTokens {
  if (typeof body.access_token !== 'string' || body.access_token === '') {
    throw new AuthError(502, 'The sign-in service did not return an access token.');
  }
  return {
    accessToken: body.access_token,
    refreshToken: typeof body.refresh_token === 'string' ? body.refresh_token : null,
    expiresAt: expiryFromResponse(body),
  };
}

async function readError(response: Response): Promise<string> {
  // A server-supplied message always wins; the fallbacks below only apply when
  // there is no usable body, which is the common case for a plain 401.
  try {
    const text = await response.text();
    if (text) {
      const body = JSON.parse(text) as { message?: unknown; error_description?: unknown };
      const message = body.message ?? body.error_description;
      if (typeof message === 'string' && message !== '') return message;
    }
  } catch {
    /* Non-JSON body. */
  }
  return response.status === 401
    ? 'Incorrect username or password.'
    : `Sign-in failed (${response.status}).`;
}

export interface PasswordAuthConfig {
  readonly loginUrl: string;
  readonly refreshUrl: string | null;
  readonly appType: string;
}

export async function login(
  config: PasswordAuthConfig,
  username: string,
  password: string,
  signal?: AbortSignal,
): Promise<PasswordTokens> {
  const init: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ username, password, appType: config.appType }),
  };
  if (signal) init.signal = signal;

  const response = await fetch(config.loginUrl, init);
  if (!response.ok) throw new AuthError(response.status, await readError(response));
  return toTokens((await response.json()) as LoginResponse);
}

export async function refresh(
  config: PasswordAuthConfig,
  refreshToken: string,
  signal?: AbortSignal,
): Promise<PasswordTokens> {
  if (!config.refreshUrl) {
    throw new AuthError(501, 'This realm does not expose a refresh endpoint.');
  }
  const init: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  };
  if (signal) init.signal = signal;

  const response = await fetch(config.refreshUrl, init);
  if (!response.ok) throw new AuthError(response.status, await readError(response));
  return toTokens((await response.json()) as LoginResponse);
}

/** Renew this far before expiry so an in-flight query never dies on a 401. */
export const RENEW_MARGIN_MS = 60_000;

export function renewDelay(expiresAt: number, now = Date.now()): number {
  // Never schedule in the past, and never busy-loop on an already-stale token.
  return Math.max(5_000, expiresAt - now - RENEW_MARGIN_MS);
}
